import {
  type AskPunttAgentStep,
  type AskPunttMessage,
} from "@mg/schemas/src/christo/askPuntt";
import {
  ArrowSquareOut,
  CaretDown,
  CaretRight,
  CheckCircle,
  CircleNotch,
  ListNumbers,
} from "@phosphor-icons/react";
import { Button, Flex, Text } from "@radix-ui/themes";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  type AskPunttArtifactContextValue,
  useAskPunttArtifactContext,
} from "../AskPunttArtifactContext";

/**
 * Tool names whose results land a chat-level artifact (ticket review or
 * canvas deliverable). Exported for chat.tsx's D8 streaming-suppression
 * predicate — when the in-flight tool_call is one of these, older
 * artifact rows grey out so a stale click can't yank the right pane
 * mid-stream. The clickability gate itself stays message-scoped (D7):
 * ANY tool_result inside a message that owns `viewTicketId` or a
 * non-empty `canvasContent` is eligible to be clickable, regardless of
 * whether the producing tool name is in this set.
 */
export const ARTIFACT_PRODUCING_TOOLS: ReadonlySet<string> = new Set([
  "create_ticket",
  "create_brief",
  "create_canvas",
  "create_roi",
  "create_research_brief",
]);

// ─── Standardized tool/skill chip ─────────────────────────────────────────────

/**
 * One shape for every tool/skill row in the stream — in-progress AND completed.
 * A bordered, rounded pill so create_ticket, search, brief generation, etc. all
 * read as the same kind of thing. The row's CONTENT (status glyph, result
 * count, summary) is unchanged from the legacy rows — only the frame is shared.
 * Callers append state classes (active/blocked/hover) after this base.
 */
/**
 * Size + spacing shared by every stream chip. Rows that should match the chip
 * FOOTPRINT but keep their own fill/border/icons (e.g. review-checklist steps)
 * reuse this alone, so padding/rounding/gap live in one place and never drift.
 */
export const TOOL_CHIP_SIZING = "gap-2 rounded-lg px-2.5 py-1.5";

/** The full standardized stream chip: chip sizing on a bordered white pill. */
export const TOOL_CHIP_CLASS = `flex w-full items-center ${TOOL_CHIP_SIZING} border border-puntt-neutral-gray-6 bg-base-white`;

interface AgentStepsProps {
  steps: AskPunttAgentStep[];
  isStreaming: boolean;
  /**
   * The parent assistant message. When omitted (legacy callers, isolated
   * tests), artifact clickability is disabled and the component behaves
   * exactly as it did pre-feature.
   */
  message?: AskPunttMessage;
}

// ─── Step pair / group types ─────────────────────────────────────────────────

type StepPair = {
  call: AskPunttAgentStep & { type: "tool_call" };
  result?: AskPunttAgentStep & { type: "tool_result" };
};

type StepGroup = {
  tool: string;
  displayName: string;
  statusName?: string;
  pairs: StepPair[];
  /** Timestamp of first call — stable key across re-renders */
  firstTimestamp: string;
};

/**
 * Groups consecutive same-tool call+result pairs into StepGroups.
 * Thinking steps are skipped (rendered as separate chat bubbles by ChatMessage).
 */
function groupConsecutiveToolSteps(steps: AskPunttAgentStep[]): StepGroup[] {
  const result: StepGroup[] = [];
  let i = 0;

  while (i < steps.length) {
    const step = steps[i];

    if (step.type === "thinking") {
      i++;
      continue;
    }

    if (step.type === "tool_call") {
      const group: StepGroup = {
        tool: step.tool ?? "",
        displayName: step.displayName ?? "",
        statusName: step.statusName,
        pairs: [],
        firstTimestamp: step.timestamp,
      };

      while (i < steps.length) {
        const current = steps[i];
        if (current.type === "tool_call" && current.tool === step.tool) {
          const pair: StepPair = {
            call: current as AskPunttAgentStep & { type: "tool_call" },
          };
          i++;
          if (i < steps.length && steps[i].type === "tool_result") {
            pair.result = steps[i] as AskPunttAgentStep & {
              type: "tool_result";
            };
            i++;
          }
          group.pairs.push(pair);
        } else {
          break;
        }
      }

      result.push(group);
      continue;
    }

    // Orphan tool_result — skip gracefully
    i++;
  }

  return result;
}

// ─── Clickability derivation ─────────────────────────────────────────────────

interface Clickability {
  kind: "ticket" | "canvas";
  isActive: boolean;
  isBlocked: boolean;
  ariaLabel: string;
  testId: string;
  onActivate: () => void;
}

function messageHasCanvas(m: AskPunttMessage | undefined | null): boolean {
  const c = m?.canvasContent as { blocks?: unknown[] } | null | undefined;
  return !!(c && Array.isArray(c.blocks) && c.blocks.length > 0);
}

function formatStepTime(ts: string | undefined): string | null {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

/**
 * Decide whether a tool_result row maps to a re-openable artifact. Returns
 * null when no provider context exists, no parent message, or the row
 * doesn't qualify. The clickability gate is message-scoped (D7) AND
 * count-gated (D6, `length > 1`) so single-artifact chats render exactly
 * as today.
 */
function deriveClickability(
  step: AskPunttAgentStep & { type: "tool_result" },
  message: AskPunttMessage | undefined,
  ctx: AskPunttArtifactContextValue | null,
): Clickability | null {
  if (!message || !ctx) return null;

  const ticketId = message.viewTicketId;
  const isTicketResult = step.tool === "create_ticket" && !!ticketId;

  if (isTicketResult && ctx.allReviewTicketIds.length > 1 && ticketId) {
    const idx = ctx.allReviewTicketIds.indexOf(ticketId);
    const total = ctx.allReviewTicketIds.length;
    const isActive = ctx.activeTicketId === ticketId;
    const isBlocked = ctx.isAnotherArtifactStreaming && !isActive;
    const verb = isActive ? "Currently viewing" : "Open";
    const positionLabel = idx >= 0 ? ` ${idx + 1} of ${total}` : "";
    const time = formatStepTime(step.timestamp);
    const timeLabel = time ? ` — created ${time}` : "";
    const blockLabel = isBlocked ? " — generating new review, please wait" : "";
    return {
      kind: "ticket",
      isActive,
      isBlocked,
      ariaLabel: `${verb} ticket${positionLabel}${timeLabel}${blockLabel}`,
      testId: `agent-step-toolresult-ticket-${ticketId}`,
      onActivate: () => ctx.onActivateTicket(ticketId),
    };
  }

  if (messageHasCanvas(message) && ctx.canvasMessageIds.length > 1) {
    const messageId = message._id;
    const isActive = ctx.activeCanvasMessageId === messageId;
    const isBlocked = ctx.isAnotherArtifactStreaming && !isActive;
    const verb = isActive ? "Currently viewing" : "Open";
    // D5 fallback chain: displayName → summary → "canvas"
    const rawName = step.displayName || step.summary || "canvas";
    const name = rawName.length > 60 ? `${rawName.slice(0, 60)}...` : rawName;
    const time = formatStepTime(step.timestamp);
    const timeLabel = time ? ` — created ${time}` : "";
    const blockLabel = isBlocked ? " — generating new review, please wait" : "";
    return {
      kind: "canvas",
      isActive,
      isBlocked,
      ariaLabel: `${verb} ${name}${timeLabel}${blockLabel}`,
      testId: `agent-step-toolresult-canvas-${messageId}`,
      onActivate: () => ctx.onActivateCanvas(messageId),
    };
  }

  return null;
}

/**
 * Used by AgentSteps to decide whether to default-expand the steps
 * accordion (D1). True when at least one tool_result is clickable AND
 * its artifact is NOT currently active — i.e. the user could navigate
 * to it. We deliberately ignore blocked rows so an in-flight new
 * artifact doesn't force-expand the older message's accordion.
 */
function groupsHaveUnviewedClickable(
  groups: StepGroup[],
  message: AskPunttMessage | undefined,
  ctx: AskPunttArtifactContextValue | null,
): boolean {
  for (const g of groups) {
    for (const pair of g.pairs) {
      if (!pair.result) continue;
      const c = deriveClickability(pair.result, message, ctx);
      if (c && !c.isActive && !c.isBlocked) return true;
    }
  }
  return false;
}

// ─── Individual step renderers ────────────────────────────────────────────────

function ToolCallStep({
  step,
}: {
  step: AskPunttAgentStep & { type: "tool_call" };
}) {
  const primaryName = step.statusName || step.displayName;
  const showOriginal = !!step.statusName;
  const secondaryText = step.description
    ? step.description
    : step.args
      ? JSON.stringify(step.args).slice(0, 80)
      : "";

  return (
    <Flex
      align="center"
      gap="2"
      className={TOOL_CHIP_CLASS}
      data-testid="agent-step"
      data-step-type="tool_call"
    >
      <CircleNotch
        size={14}
        className="shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
        aria-hidden
      />
      <Text size="2" weight="medium" className="text-puntt-neutral-gray-11">
        {primaryName}
      </Text>
      {showOriginal && (
        <Text size="1" className="text-puntt-neutral-gray-8">
          {step.displayName}
        </Text>
      )}
      {secondaryText && (
        <Text size="2" className="truncate text-puntt-neutral-gray-9">
          {secondaryText}
        </Text>
      )}
    </Flex>
  );
}

function CreateTicketProgressStep({
  step,
}: {
  step: AskPunttAgentStep & { type: "tool_call" };
}) {
  const startMs = useMemo(
    () => new Date(step.timestamp).getTime(),
    [step.timestamp],
  );
  const [elapsed, setElapsed] = useState(() =>
    Math.floor((Date.now() - startMs) / 1000),
  );

  useEffect(() => {
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startMs) / 1000));
    }, 500);
    return () => clearInterval(id);
  }, [startMs]);

  const pct = Math.min(Math.round((elapsed / 10) * 85), 85);
  const primaryName = step.statusName || step.displayName;

  return (
    <Flex
      direction="column"
      gap="2"
      className={TOOL_CHIP_CLASS}
      data-testid="agent-step"
      data-step-type="tool_call"
    >
      <Flex align="center" gap="2" className="w-full">
        <CircleNotch
          size={14}
          className="shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
          aria-hidden
        />
        <Text size="2" className="font-medium text-puntt-neutral-gray-11">
          {primaryName}
        </Text>
        <Text
          size="1"
          className="ml-auto tabular-nums text-puntt-neutral-gray-8"
        >
          {elapsed}s
        </Text>
      </Flex>
      <div className="h-1 w-full overflow-hidden rounded-full bg-puntt-neutral-gray-3">
        <div
          className="h-full rounded-full bg-puntt-accent-7 transition-[width] duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </Flex>
  );
}

function ToolResultStep({
  step,
  clickability,
}: {
  step: AskPunttAgentStep & { type: "tool_result" };
  clickability: Clickability | null;
}) {
  const summary =
    step.summary.length > 100
      ? `${step.summary.slice(0, 100)}...`
      : step.summary;
  const primaryName = step.statusName || step.displayName;

  // D9: 200ms click flash, suppressed under prefers-reduced-motion.
  const [justClicked, setJustClicked] = useState(false);
  useEffect(() => {
    if (!justClicked) return;
    const t = setTimeout(() => setJustClicked(false), 200);
    return () => clearTimeout(t);
  }, [justClicked]);

  if (!clickability) {
    return (
      <Flex
        align="center"
        gap="2"
        className={TOOL_CHIP_CLASS}
        data-testid="agent-step"
        data-step-type="tool_result"
      >
        <CheckCircle
          size={14}
          weight="fill"
          className="shrink-0 text-malachite-700"
          aria-hidden
        />
        <Text size="2" weight="medium" className="text-puntt-neutral-gray-11">
          {primaryName}
        </Text>
        {step.resultCount !== undefined && step.resultCount > 0 && (
          <Text size="2" className="text-puntt-neutral-gray-9">
            {step.resultCount} {step.resultCount === 1 ? "result" : "results"}
          </Text>
        )}
        {summary && (
          <Text size="2" className="truncate text-puntt-neutral-gray-9">
            {summary}
          </Text>
        )}
      </Flex>
    );
  }

  const { isActive, isBlocked, ariaLabel, testId, onActivate, kind } =
    clickability;

  const handleClick = () => {
    if (isBlocked || isActive) return;
    const prefersReducedMotion =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!prefersReducedMotion) setJustClicked(true);
    onActivate();
  };

  const stateClass = isActive
    ? "border-puntt-accent-9 bg-puntt-neutral-gray-2"
    : isBlocked
      ? "cursor-not-allowed opacity-60"
      : `cursor-pointer hover:bg-puntt-accent-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-puntt-accent-7 ${
          justClicked ? "bg-puntt-accent-3" : ""
        }`;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isBlocked}
      tabIndex={isBlocked ? -1 : 0}
      aria-disabled={isBlocked || undefined}
      aria-current={isActive ? "true" : undefined}
      aria-label={ariaLabel}
      data-testid={testId}
      data-step-type="tool_result"
      data-artifact-kind={kind}
      data-artifact-active={isActive ? "true" : "false"}
      data-artifact-blocked={isBlocked ? "true" : "false"}
      className={`${TOOL_CHIP_CLASS} text-left transition-colors ${stateClass}`}
    >
      <CheckCircle
        size={14}
        weight="fill"
        className="shrink-0 text-malachite-700"
        aria-hidden
      />
      <Text size="2" weight="medium" className="text-puntt-neutral-gray-11">
        {primaryName}
      </Text>
      {step.resultCount !== undefined && step.resultCount > 0 && (
        <Text size="2" className="text-puntt-neutral-gray-9">
          {step.resultCount} {step.resultCount === 1 ? "result" : "results"}
        </Text>
      )}
      {summary && (
        <Text size="2" className="truncate text-puntt-neutral-gray-9">
          {summary}
        </Text>
      )}
      {!isActive && (
        <ArrowSquareOut
          size={12}
          weight="regular"
          className="ml-auto shrink-0 text-puntt-neutral-gray-8"
          aria-hidden
        />
      )}
    </button>
  );
}

// ─── Collapsed group row ──────────────────────────────────────────────────────

function CollapsedToolGroup({
  group,
  isNew,
  streamingEndedAt,
  message,
  ctx,
}: {
  group: StepGroup;
  isNew: boolean;
  streamingEndedAt: number | null;
  message: AskPunttMessage | undefined;
  ctx: AskPunttArtifactContextValue | null;
}) {
  const [expanded, setExpanded] = useState(false);

  // Auto-collapse when streaming ends
  useEffect(() => {
    if (streamingEndedAt !== null) setExpanded(false);
  }, [streamingEndedAt]);

  const callCount = group.pairs.length;
  const primaryName = group.statusName || group.displayName;
  const lastPair = group.pairs[group.pairs.length - 1];
  const allDone = !!lastPair?.result;

  // Build summary label: show first description/query if available
  const firstCall = group.pairs[0]?.call;
  const firstQuery = firstCall?.description
    ? firstCall.description.slice(0, 30)
    : firstCall?.args
      ? JSON.stringify(firstCall.args).slice(0, 30)
      : null;

  const label =
    callCount === 1
      ? primaryName
      : firstQuery
        ? `${primaryName} — "${firstQuery}" + ${callCount - 1} more`
        : `${primaryName} (${callCount} times)`;

  if (callCount === 1) {
    const onlyPair = lastPair;
    const clickability = onlyPair?.result
      ? deriveClickability(onlyPair.result, message, ctx)
      : null;
    // Single pair — render exactly as before, no collapse chrome
    return (
      <div
        data-testid="agent-step-group"
        className={isNew ? "animate-block-fade-up" : ""}
      >
        {allDone ? (
          <ToolResultStep step={onlyPair.result!} clickability={clickability} />
        ) : onlyPair.call.tool === "create_ticket" ? (
          <CreateTicketProgressStep step={onlyPair.call} />
        ) : (
          <ToolCallStep step={onlyPair.call} />
        )}
      </div>
    );
  }

  // Multiple same-tool pairs — collapsed summary with expand toggle
  return (
    <div
      data-testid="agent-step-group"
      className={isNew ? "animate-block-fade-up" : ""}
    >
      <button
        data-testid="agent-steps-button-291"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center gap-2 rounded px-2 py-1 text-left hover:bg-puntt-neutral-gray-3"
        aria-expanded={expanded}
      >
        {allDone ? (
          <CheckCircle
            size={14}
            weight="fill"
            className="shrink-0 text-malachite-700"
          />
        ) : (
          <CircleNotch
            size={14}
            className="shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
          />
        )}
        <Text
          size="2"
          className="flex-1 truncate font-medium text-puntt-neutral-gray-11"
        >
          {label}
        </Text>
        {expanded ? (
          <CaretDown size={10} className="shrink-0 text-puntt-neutral-gray-9" />
        ) : (
          <CaretRight
            size={10}
            className="shrink-0 text-puntt-neutral-gray-9"
          />
        )}
      </button>

      <div
        className={`overflow-hidden transition-[max-height,opacity] duration-200 ${
          expanded ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="ml-4 space-y-0.5 border-l border-puntt-neutral-gray-3 pl-2">
          {group.pairs.map((pair) => {
            const pairClickability = pair.result
              ? deriveClickability(pair.result, message, ctx)
              : null;
            return (
              <div key={pair.call.timestamp}>
                {pair.result ? (
                  <ToolResultStep
                    step={pair.result}
                    clickability={pairClickability}
                  />
                ) : pair.call.tool === "create_ticket" ? (
                  <CreateTicketProgressStep step={pair.call} />
                ) : (
                  <ToolCallStep step={pair.call} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Main AgentSteps component ────────────────────────────────────────────────

export function AgentSteps({ steps, isStreaming, message }: AgentStepsProps) {
  const ctx = useAskPunttArtifactContext();
  const [manualToggle, setManualToggle] = useState<boolean | null>(null);
  const prevGroupCountRef = useRef(0);
  const wasStreamingRef = useRef(isStreaming);

  const groups = useMemo(() => groupConsecutiveToolSteps(steps), [steps]);

  // D1: when a message holds a clickable artifact the user hasn't viewed
  // yet, default the accordion to expanded post-stream so the affordance
  // is reachable without a manual click. Manual collapse wins (the
  // `manualToggle !== null` short-circuit) — once the user explicitly
  // toggles, we stop force-expanding.
  const hasUnviewedClickable = useMemo(
    () => groupsHaveUnviewedClickable(groups, message, ctx),
    [groups, message, ctx],
  );

  const expanded =
    manualToggle !== null ? manualToggle : isStreaming || hasUnviewedClickable;

  const prevGroupCount = prevGroupCountRef.current;
  useEffect(() => {
    prevGroupCountRef.current = groups.length;
  }, [groups.length]);

  // Signal per-group components to auto-collapse when streaming ends
  const [streamingEndedAt, setStreamingEndedAt] = useState<number | null>(null);
  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming) {
      setStreamingEndedAt(Date.now());
    }
    wasStreamingRef.current = isStreaming;
  }, [isStreaming]);

  const stepCount = steps.filter((s) => s.type !== "thinking").length;
  const summaryText = isStreaming
    ? "Working..."
    : `${stepCount} Step${stepCount === 1 ? "" : "s"}`;

  // Nothing to show if all steps were thinking (rendered as chat bubbles)
  if (groups.length === 0 && !isStreaming) return null;

  return (
    <div data-testid="agent-steps">
      {isStreaming ? (
        <div className="rounded-xl border border-puntt-neutral-gray-6 bg-base-white p-3 shadow-sm">
          <Flex
            align="center"
            gap="2"
            className="mb-3 border-b border-puntt-neutral-gray-6 pb-2"
          >
            <CircleNotch
              size={16}
              className="animate-spin text-puntt-accent-9 motion-reduce:animate-none"
            />
            <Text
              size="2"
              weight="medium"
              className="text-puntt-neutral-gray-12"
            >
              Working
            </Text>
          </Flex>
          <div className="ml-3 space-y-0.5 border-l-2 border-puntt-accent-6/30 pl-3">
            {groups.map((group, idx) => (
              <CollapsedToolGroup
                key={group.firstTimestamp}
                group={group}
                isNew={idx >= prevGroupCount}
                streamingEndedAt={streamingEndedAt}
                message={message}
                ctx={ctx}
              />
            ))}
          </div>
        </div>
      ) : (
        <>
          <Button
            data-testid="agent-steps-button-414"
            size="1"
            variant="outline"
            color="gray"
            className="cursor-pointer"
            onClick={() => setManualToggle(!expanded)}
            aria-expanded={expanded}
            aria-label={`${expanded ? "Collapse" : "Expand"} agent steps`}
          >
            <ListNumbers size={14} />
            {summaryText}
            {expanded ? <CaretDown size={10} /> : <CaretRight size={10} />}
          </Button>

          <div
            className={`overflow-hidden transition-[max-height,opacity] ${
              expanded
                ? "max-h-[600px] opacity-100 duration-300"
                : "max-h-0 opacity-0 duration-200"
            }`}
          >
            <div className="ml-3 space-y-0.5 border-l-2 border-puntt-accent-6/30 pl-3">
              {groups.map((group, idx) => (
                <CollapsedToolGroup
                  key={group.firstTimestamp}
                  group={group}
                  isNew={idx >= prevGroupCount}
                  streamingEndedAt={streamingEndedAt}
                  message={message}
                  ctx={ctx}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
